// src/pages/ConsultantObservation.js
// Consultant Observation summary — last 5 meetings, scored across the 4 Meeting Inventory categories.
// Visible to all roles with a teamId.

import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { MEETING_INVENTORY, scoreInventoryFromChecks } from '../utils/metrics';
import { DEFAULT_CATEGORIES } from '../utils/surveyQuestions';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
} from 'recharts';
import styles from './ConsultantObservation.module.css';
import PageLoading from '../components/PageLoading';

// ─── Colour palette per category ─────────────────────────────────────────────

const CAT_META = {
  simpleProcess:     { label: 'Simple Process',      color: '#2a3d5b', bg: '#2a3d5b18', emoji: '📋' },
  compellingContent: { label: 'Compelling Content',  color: '#5f7942', bg: '#5f794218', emoji: '💡' },
  healthyBehaviors:  { label: 'Healthy Behaviors',   color: '#cba43a', bg: '#cba43a18', emoji: '💪' },
  rightGenius:       { label: 'Right Chemistry',     color: '#7209b7', bg: '#7209b718', emoji: '✨' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pctColor(pct) {
  if (pct >= 70) return 'var(--accent)';
  if (pct >= 40) return '#cba43a';
  return '#922b3f';
}

function pctLabel(pct) {
  if (pct >= 80) return 'Strong';
  if (pct >= 60) return 'Present';
  if (pct >= 40) return 'Developing';
  if (pct >= 20) return 'Emerging';
  return 'Not Evident';
}

// ─── Score Ring ───────────────────────────────────────────────────────────────

function ScoreRing({ pct, size = 72, color }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth={6} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={6}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
      <text
        x={size / 2} y={size / 2 + 1}
        textAnchor="middle" dominantBaseline="middle"
        fontSize={size < 60 ? 11 : 14} fontWeight={700} fill={color}
      >
        {pct}%
      </text>
    </svg>
  );
}

// ─── Category Card ────────────────────────────────────────────────────────────

function CategoryCard({ catKey, groupScores, meetings }) {
  const meta = CAT_META[catKey];
  const [expanded, setExpanded] = useState(false);
  const inventory = MEETING_INVENTORY[catKey];

  // Compute per-item averages across the meetings
  const itemAverages = inventory.items.map(item => {
    const total = meetings.reduce((sum, m) => {
      return sum + (m.inventoryChecks?.[item.id] ? 1 : 0);
    }, 0);
    return {
      id: item.id,
      text: item.text,
      count: total,
      pct: meetings.length > 0 ? Math.round((total / meetings.length) * 100) : 0,
    };
  });

  // Sort: lowest first so easy to spot gaps
  const sorted = [...itemAverages].sort((a, b) => a.pct - b.pct);

  return (
    <div style={{
      borderRadius: 12,
      border: `1.5px solid ${meta.color}44`,
      background: 'var(--bg-card)',
      overflow: 'hidden',
    }}>
      {/* Header row */}
      <div
        className={styles.catCardHeader}
        style={{ borderBottom: expanded ? '1px solid var(--border)' : 'none' }}
      >
        <ScoreRing pct={groupScores.pct} size={68} color={meta.color} />
        <div className={styles.catCardContent}>
          <div className={styles.catLabelRow}>
            <span className={styles.catEmoji}>{meta.emoji}</span>
            <span className={styles.catName}>{meta.label}</span>
          </div>
          <div className={styles.catStatusRow}>
            <span
              className={styles.catBadge}
              style={{ color: meta.color, background: meta.bg }}
            >
              {pctLabel(groupScores.pct)}
            </span>
            <span className={styles.catMeta}>
              {groupScores.score} / {groupScores.max} pts across {meetings.length} meeting{meetings.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <button
          onClick={() => setExpanded(e => !e)}
          className={styles.toggleBtn}
          onMouseEnter={e => { e.currentTarget.style.borderColor = meta.color; e.currentTarget.style.color = meta.color; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--ink-muted)'; }}
          title={expanded ? 'Collapse' : 'Expand items'}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            {expanded
              ? <polyline points="18 15 12 9 6 15" />
              : <polyline points="6 9 12 15 18 9" />}
          </svg>
        </button>
      </div>

      {/* Item breakdown */}
      {expanded && (
        <div className={styles.itemBreakdown}>
          <div className={styles.itemBreakdownHeader}>
            Item Breakdown — observed in last {meetings.length} meeting{meetings.length !== 1 ? 's' : ''}
          </div>
          {sorted.map(item => (
            <div key={item.id}>
              <div className={styles.itemRow}>
                <span className={styles.itemText}>{item.text}</span>
                <span className={styles.itemCount} style={{ color: pctColor(item.pct) }}>
                  {item.count}/{meetings.length}
                </span>
              </div>
              <div className={styles.itemTrack}>
                <div className={styles.itemFill} style={{ width: `${item.pct}%`, background: meta.color }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Trend Bar ────────────────────────────────────────────────────────────────

function TrendRow({ meeting, index, total }) {
  const scores = meeting._scores;
  if (!scores) return null;

  const date = meeting.meetingDate?.toDate
    ? meeting.meetingDate.toDate()
    : meeting.meetingDate
    ? new Date(meeting.meetingDate)
    : null;

  const dateStr = date
    ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : `Meeting ${total - index}`;

  return (
    <div className={styles.trendRow}>
      <div className={styles.trendDate}>{dateStr}</div>
      <div className={styles.trendGrid}>
        {Object.entries(CAT_META).map(([key, meta]) => {
          const g = scores.groups[key];
          if (!g) return null;
          return (
            <div key={key} className={styles.trendCatCell}>
              <div className={styles.trendCatPct} style={{ color: pctColor(g.pct) }}>{g.pct}%</div>
              <div className={styles.trendTrack}>
                <div className={styles.trendFill} style={{ width: `${g.pct}%`, background: meta.color }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.trendTotal} style={{ color: pctColor(scores.pct) }}>
        {scores.pct}%
      </div>
    </div>
  );
}

// ─── Perception vs Observation ──────────────────────────────────────────────

function PerceptionVsObservation({ resolvedTeamId, aggregateScores }) {
  const [surveyPcts, setSurveyPcts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!resolvedTeamId) return;
    async function loadSurveyData() {
      try {
        const snap = await getDocs(
          query(collection(db, 'responses'), where('teamId', '==', resolvedTeamId))
        );
        const responses = snap.docs.map(d => d.data());
        if (responses.length === 0) { setLoading(false); return; }

        // Average each category's questions (1–5 scale) then convert to 0–100
        const pcts = {};
        DEFAULT_CATEGORIES.forEach(cat => {
          const allScores = [];
          responses.forEach(r => {
            cat.questions.forEach(q => {
              if (r.answers?.[q.id] != null) allScores.push(r.answers[q.id]);
            });
          });
          if (allScores.length > 0) {
            const avg = allScores.reduce((a, b) => a + b, 0) / allScores.length;
            // Convert 1–5 scale → 0–100%
            pcts[cat.id] = Math.round(((avg - 1) / 4) * 100);
          } else {
            pcts[cat.id] = null;
          }
        });
        setSurveyPcts(pcts);
      } catch (err) {
        console.error('Error loading survey data for comparison:', err);
      } finally {
        setLoading(false);
      }
    }
    loadSurveyData();
  }, [resolvedTeamId]);

  if (loading || !surveyPcts || !aggregateScores) return null;

  // Check we have data for at least one category in both sources
  const hasSurvey = Object.values(surveyPcts).some(v => v !== null);
  const hasObs = aggregateScores?.groups && Object.values(aggregateScores.groups).some(g => g.pct > 0);
  if (!hasSurvey || !hasObs) return null;

  const catKeys = Object.keys(CAT_META);

  // Bar chart data — one bar group per category
  const barData = catKeys.map(key => ({
    category: CAT_META[key].label,
    shortLabel: CAT_META[key].label.split(' ').slice(-1)[0], // last word for mobile
    perception: surveyPcts[key] ?? 0,
    observation: aggregateScores.groups[key]?.pct ?? 0,
    color: CAT_META[key].color,
  }));

  // Compute gap labels for each category
  const gaps = catKeys.map(key => {
    const p = surveyPcts[key] ?? 0;
    const o = aggregateScores.groups[key]?.pct ?? 0;
    const diff = p - o;
    return { key, label: CAT_META[key].label, color: CAT_META[key].color, perception: p, observation: o, diff };
  }).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const p = payload.find(x => x.dataKey === 'perception')?.value ?? 0;
    const o = payload.find(x => x.dataKey === 'observation')?.value ?? 0;
    const diff = p - o;
    return (
      <div className={styles.tooltipBox}>
        <div className={styles.tooltipTitle}>{label}</div>
        <div className={styles.tooltipRow}>
          <span className={styles.tooltipPerception}>Team Perception</span>
          <span style={{ fontWeight: 700 }}>{p}%</span>
        </div>
        <div className={styles.tooltipRowLast}>
          <span className={styles.tooltipObservation}>Consultant Observed</span>
          <span style={{ fontWeight: 700 }}>{o}%</span>
        </div>
        <div
          className={styles.tooltipGapRow}
          style={{ color: diff > 8 ? '#e63946' : diff < -8 ? '#2d6a4f' : 'var(--ink-muted)' }}
        >
          <span>Gap</span>
          <span>{diff > 0 ? '+' : ''}{diff}%</span>
        </div>
      </div>
    );
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-title" style={{ marginBottom: 4 }}>Team Perception vs. Consultant Observation</div>
      <div className="card-subtitle" style={{ marginBottom: 24 }}>
        How the team rates itself compared to what the consultant observed across the last {' '}
        meetings — both scaled to 0–100%.
      </div>

      {/* Bar chart */}
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={barData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }} barGap={4} barCategoryGap="30%">
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="category"
            tick={{ fontSize: 11, fill: 'var(--ink-muted)', fontWeight: 500 }}
            axisLine={false} tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            tickFormatter={v => `${v}%`}
            tick={{ fontSize: 11, fill: 'var(--ink-muted)' }}
            axisLine={false} tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '0.78rem', paddingTop: 16 }}
            formatter={(value) => <span style={{ color: 'var(--ink-soft)' }}>{value}</span>}
          />
          <Bar dataKey="perception" name="Team Perception" fill="#2a3d5b" radius={[4, 4, 0, 0]} fillOpacity={0.85} />
          <Bar dataKey="observation" name="Consultant Observation" fill="#5f7942" radius={[4, 4, 0, 0]} fillOpacity={0.85} />
        </BarChart>
      </ResponsiveContainer>

      {/* Gap analysis rows */}
      <div className={styles.gapAnalysisHeader}>
        <div className={styles.gapAnalysisLabel}>Gap Analysis — largest gaps first</div>
        {gaps.map(g => {
          const isOverconfident = g.diff > 0;
          const isUnderconfident = g.diff < 0;
          const gapAbs = Math.abs(g.diff);
          const significant = gapAbs >= 10;
          return (
            <div
              key={g.key}
              className={styles.gapRowInner}
              style={{
                background: significant ? (isOverconfident ? 'rgba(230,57,70,0.05)' : 'rgba(45,106,79,0.05)') : 'var(--bg-soft)',
                border: significant ? `1px solid ${isOverconfident ? '#e6394622' : '#2d6a4f22'}` : '1px solid transparent',
              }}
            >
              <div className={styles.gapDot} style={{ background: g.color }} />
              <div className={styles.gapRowLabel}>{g.label}</div>
              <div className={styles.gapNums}>
                <div className={styles.gapNumCol}>
                  <div className={styles.teamColLabel}>Team</div>
                  <div className={styles.teamColValue}>{g.perception}%</div>
                </div>
                <div className={styles.gapNumCol}>
                  <div className={styles.obsColLabel}>Observed</div>
                  <div className={styles.obsColValue}>{g.observation}%</div>
                </div>
                <div
                  className={styles.gapColBox}
                  style={{ background: gapAbs < 5 ? 'var(--bg-soft)' : isOverconfident ? 'rgba(230,57,70,0.1)' : 'rgba(45,106,79,0.1)' }}
                >
                  <div className={styles.gapColLabel}>Gap</div>
                  <div
                    className={styles.gapColValue}
                    style={{ color: gapAbs < 5 ? 'var(--ink-muted)' : isOverconfident ? '#e63946' : '#2d6a4f' }}
                  >
                    {g.diff > 0 ? '+' : ''}{g.diff}%
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Key */}
      <div className={styles.keyBox}>
        <span className={styles.keyPositive}>Positive gap</span> = team rates itself higher than observed &nbsp;·&nbsp;
        <span className={styles.keyNegative}>Negative gap</span> = team underestimates what the consultant sees
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ConsultantObservation() {
  const { currentUser, userProfile } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolvedTeamId, setResolvedTeamId] = useState(null);

  // Team leaders are identified by leaderEmail on the team doc, not by teamId on their profile.
  // Resolve the teamId for all roles.
  useEffect(() => {
    async function resolveTeamId() {
      const role = userProfile?.role;
      // team_member, consultant, admin — teamId is on their profile
      if (userProfile?.teamId) {
        setResolvedTeamId(userProfile.teamId);
        return;
      }
      // team_leader — find team where leaderEmail matches
      if (role === 'team_leader' && currentUser?.email) {
        try {
          const snap = await getDocs(
            query(collection(db, 'teams'), where('leaderEmail', '==', currentUser.email))
          );
          if (!snap.empty) {
            setResolvedTeamId(snap.docs[0].id);
            return;
          }
        } catch (err) {
          console.error('Error resolving teamId for team leader:', err);
        }
      }
      setResolvedTeamId(null);
      setLoading(false); // no team to load — show the "no team" state, not the spinner
    }
    if (userProfile) resolveTeamId();
  }, [userProfile, currentUser]);

  useEffect(() => { if (resolvedTeamId) loadMeetings(resolvedTeamId); }, [resolvedTeamId]);

  async function loadMeetings(teamId) {
    setLoading(true);
    try {
      // Query by teamId + status only (avoids needing a composite index on meetingDate)
      const q = query(
        collection(db, 'meetings'),
        where('teamId', '==', teamId),
        where('status', 'in', ['completed', 'reviewed']),
        limit(20)
      );
      const snap = await getDocs(q);
      // Sort client-side by meetingDate desc, take 5 most recent
      const docs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(m => m.inventoryChecks && Object.keys(m.inventoryChecks).length > 0)
        .sort((a, b) => {
          const da = a.meetingDate?.toDate ? a.meetingDate.toDate() : a.meetingDate ? new Date(a.meetingDate) : new Date(a.createdAt?.seconds * 1000 || 0);
          const db2 = b.meetingDate?.toDate ? b.meetingDate.toDate() : b.meetingDate ? new Date(b.meetingDate) : new Date(b.createdAt?.seconds * 1000 || 0);
          return db2 - da;
        })
        .slice(0, 5);

      // Score each meeting
      const scored = docs.map((meeting, i) => {
        const prevMeetings = docs.slice(i + 1);
        const checks = meeting.inventoryChecks || {};
        const scores = scoreInventoryFromChecks(checks, prevMeetings);
        return { ...meeting, _scores: scores };
      });

      setMeetings(scored);
    } catch (err) {
      console.error('Error loading consultant observation data:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <PageLoading text="Loading observation data..." />;
  }

  if (!resolvedTeamId) {
    return (
      <div className={styles.noTeam}>
        You're not currently assigned to a team. Observation data will appear here once you join one.
      </div>
    );
  }

  if (meetings.length === 0) {
    return (
      <div className={`fade-up ${styles.pageWrapper}`}>
        <div className="report-header" style={{ marginBottom: 32 }}>
          <div className="report-header-meta">Consultant Observation</div>
          <h2>Meeting Inventory Scores</h2>
          <div className="report-header-sub">
            Scores across the last 5 meetings based on the consultant's Meeting Inventory observations.
          </div>
        </div>
        <div className={styles.emptyMeetings}>
          No completed meetings with inventory data yet. Scores will appear here once a consultant has completed the Meeting Inventory for at least one meeting.
        </div>
      </div>
    );
  }

  // Aggregate totals across all loaded meetings
  // For the "overall" view we score as if the most recent meeting is current and others are prior
  const aggregateScores = meetings[0]?._scores;

  // Build radar chart data from aggregate
  const radarData = Object.entries(CAT_META).map(([key, meta]) => ({
    category: meta.label,
    score: aggregateScores?.groups[key]?.pct || 0,
    fullMark: 100,
  }));

  // Overall pct across all 4 categories (simple average of category pcts)
  const overallPct = aggregateScores?.pct || 0;

  return (
    <div className={`fade-up ${styles.pageWrapper}`}>
      {/* Page Header */}
      <div className="report-header" style={{ marginBottom: 32 }}>
        <div className="report-header-meta">Consultant Observation</div>
        <h2>Meeting Inventory Scores</h2>
        <div className="report-header-sub">
          Based on the last {meetings.length} meeting{meetings.length !== 1 ? 's' : ''} observed by your consultant.
        </div>
      </div>

      {/* Overall score + radar side by side */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className={styles.overallFlex}>
          {/* Overall score ring */}
          <div className={styles.scoreRingCol}>
            <ScoreRing pct={overallPct} size={100} color={pctColor(overallPct)} />
            <div className={styles.overallLabel}>Overall</div>
            <div style={{
              fontSize: '0.78rem', fontWeight: 700, color: pctColor(overallPct),
              padding: '2px 10px', borderRadius: 100,
              background: pctColor(overallPct) + '18',
            }}>
              {pctLabel(overallPct)}
            </div>
          </div>

          {/* Radar */}
          <div className={styles.radarCol}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="var(--border)" />
                <PolarAngleAxis
                  dataKey="category"
                  tick={{ fontSize: 11, fill: 'var(--ink-muted)', fontWeight: 500 }}
                />
                <Radar
                  dataKey="score"
                  stroke="#2a3d5b"
                  fill="#2a3d5b"
                  fillOpacity={0.18}
                  strokeWidth={2}
                />
                <Tooltip
                  formatter={(val) => [`${val}%`, 'Score']}
                  contentStyle={{ fontSize: '0.8rem', borderRadius: 8, border: '1px solid var(--border)' }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Quick legend */}
          <div className={styles.legendCol}>
            {Object.entries(CAT_META).map(([key, meta]) => {
              const g = aggregateScores?.groups[key];
              return (
                <div key={key} className={styles.legendItem}>
                  <div className={styles.legendDot} style={{ background: meta.color }} />
                  <div className={styles.legendText}>
                    <div className={styles.legendName}>{meta.label}</div>
                    <div className={styles.legendPct} style={{ color: pctColor(g?.pct || 0) }}>{g?.pct || 0}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Category cards */}
      <div className={styles.catCardsList}>
        {Object.keys(CAT_META).map(key => (
          <CategoryCard
            key={key}
            catKey={key}
            groupScores={aggregateScores?.groups[key] || { score: 0, max: 0, pct: 0 }}
            meetings={meetings}
          />
        ))}
      </div>

      {/* Team Perception vs Consultant Observation */}
      <PerceptionVsObservation resolvedTeamId={resolvedTeamId} aggregateScores={aggregateScores} />

      {/* Meeting-by-meeting trend */}
      {meetings.length > 1 && (
        <div className="card">
          <div className="card-title">Meeting Trend</div>
          <div className="card-subtitle">Score breakdown across each of the last {meetings.length} meetings</div>

          {/* Column headers */}
          <div className={styles.trendHeaderRow}>
            <div className={styles.trendHeaderSpacer} />
            <div className={styles.trendHeaderGrid}>
              {Object.entries(CAT_META).map(([key, meta]) => (
                <div key={key} className={styles.trendHeaderEmoji} style={{ color: meta.color }}>
                  {meta.emoji}
                </div>
              ))}
            </div>
            <div className={styles.trendHeaderAll}>All</div>
          </div>

          {meetings.map((meeting, i) => (
            <TrendRow key={meeting.id} meeting={meeting} index={i} total={meetings.length} />
          ))}

          {/* Legend */}
          <div className={styles.trendLegend}>
            {Object.entries(CAT_META).map(([key, meta]) => (
              <div key={key} className={styles.trendLegendItem}>
                <div className={styles.trendLegendDot} style={{ background: meta.color }} />
                {meta.emoji} {meta.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
